import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { listCampaigns, listDrafts } from "../lib/emailApi";
import type {
  CampaignListItemDto,
  CampaignStatus,
  MessageType,
} from "../types/campaign";

const STATUS_META: Record<CampaignStatus, { label: string; cls: string }> = {
  queued: { label: "Queued", cls: "pill--queued" },
  sending: { label: "Sending", cls: "pill--processing" },
  completed: { label: "Completed", cls: "pill--completed" },
  partial_failed: { label: "Partial failure", cls: "pill--partial" },
  failed: { label: "Failed", cls: "pill--failed" },
};

const MSG_TYPE_COLORS: Record<MessageType, string> = {
  general: "#2e7df6",
  important: "#e07a00",
  emergency: "#e10600",
};

function StatusPill({
  status,
  isDraft,
}: {
  status: CampaignStatus;
  isDraft: boolean;
}) {
  if (isDraft) {
    return <span className="pill pill--draft">Draft</span>;
  }

  const meta = STATUS_META[status] ?? { label: status, cls: "pill--queued" };
  return <span className={`pill ${meta.cls}`}>{meta.label}</span>;
}

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function deliveryBar(campaign: CampaignListItemDto) {
  if (!campaign.totalCount) return null;

  const sentPct = Math.round((campaign.sentCount / campaign.totalCount) * 100);
  const failPct = Math.round((campaign.failedCount / campaign.totalCount) * 100);

  return (
    <div className="delivery-bar">
      <div
        className="delivery-bar__fill delivery-bar__fill--sent"
        style={{ width: `${sentPct}%` }}
        title={`${campaign.sentCount} sent`}
      />
      <div
        className="delivery-bar__fill delivery-bar__fill--failed"
        style={{ width: `${failPct}%` }}
        title={`${campaign.failedCount} failed`}
      />
    </div>
  );
}

function CampaignCard({ campaign }: { campaign: CampaignListItemDto }) {
  const msgColor = campaign.messageType
    ? MSG_TYPE_COLORS[campaign.messageType]
    : "#6d6463";

  return (
    <Link to={`/campaigns/${campaign.campaignId}`} className="campaign-card">
      <div className="campaign-card__top">
        <div className="campaign-card__left">
          {campaign.messageType && (
            <span
              className="campaign-card__type"
              style={{ color: msgColor, borderColor: msgColor }}
            >
              {campaign.messageType.toUpperCase()}
            </span>
          )}
          <h3 className="campaign-card__subject">{campaign.subject || "(no subject)"}</h3>
          <p className="campaign-card__date">
            {campaign.isDraft
              ? `Draft · updated ${formatDate(campaign.updatedAt)}`
              : campaign.scheduledAt
                ? `Scheduled for ${formatDate(campaign.scheduledAt)}`
                : `Created ${formatDate(campaign.createdAt)}`}
          </p>
        </div>
        <div className="campaign-card__right">
          <StatusPill status={campaign.status} isDraft={campaign.isDraft} />
          <span className="campaign-card__stores">{campaign.totalCount} stores</span>
        </div>
      </div>

      {!campaign.isDraft && campaign.totalCount > 0 && (
        <div className="campaign-card__delivery">
          {deliveryBar(campaign)}
          <div className="campaign-card__counts">
            <span className="campaign-card__count campaign-card__count--sent">
              {campaign.sentCount} sent
            </span>
            <span className="campaign-card__count campaign-card__count--failed">
              {campaign.failedCount} failed
            </span>
            <span className="campaign-card__count">{campaign.totalCount} total</span>
          </div>
        </div>
      )}

      {campaign.tags && campaign.tags.length > 0 && (
        <div className="tag-row" style={{ marginTop: "0.75rem" }}>
          {campaign.tags.map((tag) => (
            <span key={tag} className="tag">
              {tag}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

type Tab = "sent" | "drafts";

export default function CampaignsPage() {
  const [tab, setTab] = useState<Tab>("sent");
  const [sent, setSent] = useState<CampaignListItemDto[]>([]);
  const [drafts, setDrafts] = useState<CampaignListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [continuationToken, setContinuationToken] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  const loadSent = useCallback(async (token?: string) => {
    try {
      const data = await listCampaigns({
        isDraft: false,
        pageSize: 20,
        continuationToken: token,
      });
      setSent((prev) => (token ? [...prev, ...data.items] : data.items));
      setContinuationToken(data.continuationToken);
      setHasMore(Boolean(data.continuationToken));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load campaigns.");
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    try {
      const data = await listDrafts();
      setDrafts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load drafts.");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([loadSent(), loadDrafts()]).finally(() => setLoading(false));
  }, [loadSent, loadDrafts]);

  const items = tab === "sent" ? sent : drafts;

  return (
    <AppShell
      title="Campaign history and drafts"
      eyebrow="Campaign history"
      description="Review sent campaigns, track delivery progress per store, retry failed sends, and continue editing drafts."
      showBackLink
    >
      <section className="section">
        <div className="tab-strip">
          <button
            className={`tab-btn${tab === "sent" ? " tab-btn--active" : ""}`}
            onClick={() => setTab("sent")}
          >
            Sent campaigns
            {sent.length > 0 && <span className="tab-badge">{sent.length}</span>}
          </button>
          <button
            className={`tab-btn${tab === "drafts" ? " tab-btn--active" : ""}`}
            onClick={() => setTab("drafts")}
          >
            Drafts
            {drafts.length > 0 && <span className="tab-badge">{drafts.length}</span>}
          </button>
          <Link
            to="/communications"
            className="button button--primary"
            style={{ marginLeft: "auto" }}
          >
            + New communication
          </Link>
        </div>

        {loading && <div className="page-loading">Loading campaigns…</div>}

        {error && (
          <div className="status-banner status-banner--error" style={{ marginTop: "1rem" }}>
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="empty-state">
            <p>{tab === "sent" ? "No sent campaigns yet." : "No drafts saved yet."}</p>
            <Link to="/communications" className="button button--primary">
              Create a communication
            </Link>
          </div>
        )}

        <div className="campaign-list">
          {items.map((campaign) => (
            <CampaignCard key={campaign.campaignId} campaign={campaign} />
          ))}
        </div>

        {tab === "sent" && hasMore && (
          <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
            <button
              className="button button--ghost"
              onClick={() => loadSent(continuationToken)}
            >
              Load more
            </button>
          </div>
        )}
      </section>
    </AppShell>
  );
}
